import { Button } from '@/components/button';
import { FormLabel } from '@/components/form/formlabel';
import { Input } from '@/components/form/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/form/select';
import { Modal } from '@/components/modal';
import { clearErrors } from '@/reducers/ots';
import { AppDispatch } from '@/root/store';
import { DashboardCard } from '@/routes/org/dashboard/_card';
import { OrgOtsSystem } from '@/routes/org/event/slug/ots/payments';
import { FormatCurrency } from '@/routes/root';
import { ShowEvent } from '@/services/events';
import {
  CheckBankAccount,
  GetOtsPaymentChannels,
  ShowBalance,
  TopupGuarantee,
  WithdrawGuarantee,
} from '@/services/ots';
import { NoSymbolIcon } from '@heroicons/react/24/outline';
import {
  ArrowDownOnSquareIcon,
  CreditCardIcon,
  DocumentCheckIcon,
} from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

declare global {
  interface Window {
    snap: any;
  }
}

export function OrgOtsSystemLayout() {
  const [accountChecked, setAccountChecked] = useState(false);
  const [withdrawModal, setWithdrawModal] = useState(false);
  const [collateralModal, setCollateralModal] = useState(false);
  const [formData, setFormData] = useState({});
  const { slug } = useParams();
  const dispatch: AppDispatch = useDispatch();

  const { event } = useSelector((state: any) => state.event);
  const { channels, payment, balance, validatedBank, errors } = useSelector(
    (state: any) => state.ots
  );

  const setData = (value: any, element: string) => {
    setFormData({ ...formData, [element]: value });

    setAccountChecked(false);
    dispatch(clearErrors());
  };

  const topup = () => {
    dispatch(TopupGuarantee(event.id, formData));
  };

  const withdraw = () => {
    dispatch(WithdrawGuarantee(event.id, formData));

    setWithdrawModal(false);
    dispatch(ShowBalance(slug));
  };

  const checkAccount = () => {
    dispatch(CheckBankAccount(event.id, formData));
  };

  useEffect(() => {
    if (payment?.redirect_uri && collateralModal) {
      window?.snap.pay(payment?.redirect_uri, {
        onSuccess: (result: any) => {
          toast.success('Topup berhasil diterima');

          dispatch(ShowBalance(slug));
          setCollateralModal(false);
        },
        onPending: (result: any) => {
          toast.error('Topup belum diterima');
        },
        onError: (result: any) => {
          toast.error('Topup gagal');
        },
        onClose: () => {
          toast.error('Mohon selesaikan topup dalam 15 menit');
        },
      });
    }
  }, [payment]);

  useEffect(() => {
    dispatch(GetOtsPaymentChannels(slug));
    dispatch(ShowBalance(slug));
  }, [slug]);

  useEffect(() => {
    if (validatedBank?.account_name) {
      setFormData({
        ...formData,
        account_holder_name: validatedBank?.account_name,
      });

      setAccountChecked(true);
    }
  }, [validatedBank]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = process.env.REACT_APP_MIDTRANS_URL ?? '';
    // @ts-ignore
    script.setAttribute(
      'data-client-key',
      // @ts-ignore
      process.env.REACT_APP_MIDTRANS_CLIENT_ID
    );
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <>
      <Modal
        show={withdrawModal}
        onRequestClose={() => setWithdrawModal(false)}
        width='medium'
        title='Tarik saldo'
        body={
          <div className='flex flex-col gap-4'>
            <FormLabel
              label='Jumlah saldo yang ingin ditarik'
              helperText='Minimal penarikan Rp10.000'
              errorText={errors.amount}
              required
            >
              <Input
                leftDeco='Rp'
                type='price'
                onChange={(e) => setData(e.target.value, 'amount')}
                required
              />
            </FormLabel>

            <FormLabel label='Pilih bank' errorText={errors.bank_code} required>
              <Select
                onValueChange={(e) => {
                  setData(e, 'bank_code');
                }}
              >
                <SelectTrigger>
                  <SelectValue defaultValue='others' />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value={'bri'}>Bank BRI</SelectItem>
                    <SelectItem value={'bni'}>Bank BNI</SelectItem>
                    <SelectItem value={'bca'}>Bank BCA</SelectItem>
                    <SelectItem value={'mandiri'}>Bank Mandiri</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </FormLabel>

            <FormLabel
              label='No. rekening tujuan'
              errorText={errors.account_number}
              successText={validatedBank?.account_name}
              required
            >
              <Input
                type='text'
                onChange={(e) => setData(e.target.value, 'account_number')}
                rightDeco={
                  <span onClick={checkAccount} className={'cursor-pointer'}>
                    Cek
                  </span>
                }
                required
              />
            </FormLabel>
          </div>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() => setWithdrawModal(false)}
              leftIcon={<NoSymbolIcon />}
            >
              Batal
            </Button>
            <Button
              disabled={!accountChecked}
              onClick={withdraw}
              leftIcon={<DocumentCheckIcon />}
            >
              Simpan
            </Button>
          </div>
        }
      />
      <Modal
        show={collateralModal}
        onRequestClose={() => setCollateralModal(false)}
        title='Top up dana jaminan'
        body={
          <div className='flex flex-col gap-4'>
            <FormLabel label='Jumlah' errorText={errors.amount} required>
              <Input
                leftDeco='Rp'
                type='text'
                onChange={(e: any) => {
                  setData(e.target.value, 'amount');
                }}
                required
              />
            </FormLabel>

            <FormLabel
              label='Pilih metode topup'
              required
              errorText={errors.channel}
            >
              <Select
                onValueChange={(value) => {
                  setData(value, 'channel');
                }}
              >
                <SelectTrigger>
                  <SelectValue defaultValue='other_qris' />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {channels
                      ?.filter((item: any) => !['cash'].includes(item.code))
                      ?.map((channel: any, index: string) => (
                        <SelectItem key={index} value={channel.code}>
                          {channel.name}
                        </SelectItem>
                      ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </FormLabel>
          </div>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() => setCollateralModal(false)}
              leftIcon={<NoSymbolIcon />}
            >
              Batal
            </Button>
            <Button
              onClick={() => {
                topup();
              }}
              leftIcon={<DocumentCheckIcon />}
            >
              Simpan
            </Button>
          </div>
        }
      />

      <div className='flex gap-6'>
        <DashboardCard
          variant='whiteAlt'
          name='Jaminan pembayaran cash'
          topLabel={{ primary: <FormatCurrency amount={balance} /> }}
          bottomLabel={{
            primary: (
              <div className={'flex gap-2'}>
                <Button
                  onClick={() => setCollateralModal(true)}
                  size='sm'
                  leftIcon={<CreditCardIcon />}
                >
                  Topup
                </Button>
                <Button
                  onClick={() => setWithdrawModal(true)}
                  size='sm'
                  leftIcon={<ArrowDownOnSquareIcon />}
                >
                  Tarik
                </Button>
              </div>
            ),
          }}
          className='w-[268px]'
        />
      </div>

      <OrgOtsSystem />
    </>
  );
}
